import type {
  FilterDateRange,
  FilterEnumValue,
  FilterField,
  FilterFieldValue,
  FilterNumberRange,
} from '@parcel/filters';
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Input,
  cn,
} from '@parcel/ui';
import { Check } from 'lucide-react';
import * as React from 'react';
import { DATE_PRESETS, presetRange } from './filter-menu.js';

interface FilterValueSelectorProps {
  field: FilterField;
  value: FilterFieldValue | undefined;
  options: readonly FilterEnumValue[];
  onToggleValue: (value: string) => void;
  onApplyRange: (range: FilterDateRange | FilterNumberRange) => void;
  onRemove: () => void;
}

export function FilterValueSelector({
  field,
  value,
  options,
  onToggleValue,
  onApplyRange,
  onRemove,
}: FilterValueSelectorProps) {
  if (field.type === 'date-range') {
    const range = !Array.isArray(value) && value ? (value as FilterDateRange) : {};
    return (
      <DateRangeSelector
        fieldLabel={field.label}
        range={range}
        onApply={onApplyRange}
        onRemove={onRemove}
      />
    );
  }

  if (field.type === 'number-range') {
    const range = !Array.isArray(value) && value ? (value as FilterNumberRange) : {};
    return (
      <NumberRangeSelector
        fieldLabel={field.label}
        unit={field.unit}
        range={range}
        onApply={onApplyRange}
        onRemove={onRemove}
      />
    );
  }

  const selected = new Set(Array.isArray(value) ? value : []);
  return (
    <Command>
      <CommandInput placeholder={`${field.label}…`} autoFocus />
      <CommandList className="max-h-[min(360px,50vh)]">
        <CommandEmpty>No values found.</CommandEmpty>
        <CommandGroup>
          {options.map((option) => {
            const isSelected = selected.has(option.value);
            return (
              <CommandItem
                key={option.value}
                value={`${option.label} ${option.value}`}
                onSelect={() => onToggleValue(option.value)}
              >
                <span
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-primary',
                    isSelected
                      ? 'bg-primary text-primary-foreground'
                      : 'opacity-50 [&_svg]:invisible',
                  )}
                >
                  <Check className="h-3 w-3" />
                </span>
                <span className="flex min-w-0 flex-col">
                  <span className="truncate">{option.label}</span>
                  {option.description ? (
                    <span className="truncate text-xs text-muted-foreground">
                      {option.description}
                    </span>
                  ) : null}
                </span>
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
      <div className="border-t p-1">
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-center text-muted-foreground"
          onClick={onRemove}
        >
          Remove filter
        </Button>
      </div>
    </Command>
  );
}

/**
 * A date range needs at least one side, and `after` strictly before `before`.
 *
 * Strictly, because `after` compiles to >= and `before` to < (see projectDetectedFilter
 * in the API): after === before can never match a row. The saved-View schema rejects the
 * same range, so allowing Apply here only produced an empty table and then a View that
 * refused to save.
 */
export function dateRangeApplyDisabled(after: string, before: string): boolean {
  if (!after && !before) return true;
  return Boolean(after && before && after >= before);
}

function DateRangeSelector({
  fieldLabel,
  range,
  onApply,
  onRemove,
}: {
  fieldLabel: string;
  range: FilterDateRange;
  onApply: (range: FilterDateRange) => void;
  onRemove: () => void;
}) {
  const id = React.useId();
  const afterId = `${id}-after`;
  const beforeId = `${id}-before`;
  const [after, setAfter] = React.useState(range.after ?? '');
  const [before, setBefore] = React.useState(range.before ?? '');

  return (
    <div className="flex w-full min-w-[260px] flex-col gap-1 p-2 text-sm">
      <div className="px-1 py-1 text-xs font-medium text-muted-foreground">{fieldLabel}</div>
      {DATE_PRESETS.map((preset) => (
        <Button
          key={preset.key}
          variant="ghost"
          size="sm"
          className="justify-start"
          onClick={() => onApply(presetRange(preset.key, new Date()))}
        >
          {preset.label}
        </Button>
      ))}
      <div className="my-1 h-px bg-border" />
      <label
        htmlFor={afterId}
        className="flex items-center justify-between gap-2 px-1 text-xs text-muted-foreground"
      >
        After
        <Input
          id={afterId}
          type="date"
          value={after}
          onChange={(e) => setAfter(e.target.value)}
          className="h-8 w-[150px]"
        />
      </label>
      <label
        htmlFor={beforeId}
        className="flex items-center justify-between gap-2 px-1 text-xs text-muted-foreground"
      >
        Before
        <Input
          id={beforeId}
          type="date"
          value={before}
          onChange={(e) => setBefore(e.target.value)}
          className="h-8 w-[150px]"
        />
      </label>
      <div className="mt-1 flex items-center justify-between">
        <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={onRemove}>
          Remove filter
        </Button>
        <Button
          size="sm"
          disabled={dateRangeApplyDisabled(after, before)}
          onClick={() => onApply({ after: after || undefined, before: before || undefined })}
        >
          Apply
        </Button>
      </div>
    </div>
  );
}

function NumberRangeSelector({
  fieldLabel,
  unit,
  range,
  onApply,
  onRemove,
}: {
  fieldLabel: string;
  unit?: string;
  range: FilterNumberRange;
  onApply: (range: FilterNumberRange) => void;
  onRemove: () => void;
}) {
  const id = React.useId();
  const minId = `${id}-min`;
  const maxId = `${id}-max`;
  const [min, setMin] = React.useState(range.min ?? '');
  const [max, setMax] = React.useState(range.max ?? '');
  // State/URL values are digit-only strings; strip anything else as it's typed.
  const sanitize = (raw: string) => raw.replace(/\D/g, '');

  return (
    <div className="flex w-full min-w-[260px] flex-col gap-1 p-2 text-sm">
      <div className="px-1 py-1 text-xs font-medium text-muted-foreground">
        {fieldLabel}
        {unit ? ` (${unit})` : null}
      </div>
      <label
        htmlFor={minId}
        className="flex items-center justify-between gap-2 px-1 text-xs text-muted-foreground"
      >
        Min
        <Input
          id={minId}
          type="number"
          min={0}
          value={min}
          onChange={(e) => setMin(sanitize(e.target.value))}
          className="h-8 w-[150px]"
        />
      </label>
      <label
        htmlFor={maxId}
        className="flex items-center justify-between gap-2 px-1 text-xs text-muted-foreground"
      >
        Max
        <Input
          id={maxId}
          type="number"
          min={0}
          value={max}
          onChange={(e) => setMax(sanitize(e.target.value))}
          className="h-8 w-[150px]"
        />
      </label>
      <div className="mt-1 flex items-center justify-between">
        <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={onRemove}>
          Remove filter
        </Button>
        <Button
          size="sm"
          disabled={(!min && !max) || Boolean(min && max && Number(min) > Number(max))}
          onClick={() => onApply({ min: min || undefined, max: max || undefined })}
        >
          Apply
        </Button>
      </div>
    </div>
  );
}
